import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import {
  MAX_UPLOAD_SIZE_MB,
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_IMAGE_EXTENSIONS_RE,
  ACCEPTED_IMAGE_INPUT,
  UPLOAD_HINT_TEXT_SINGLE,
} from './conceptImageFormats';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface InsightsFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  dynamicSegmentationId?: number | null;
  dynamicSegmentIndices?: number[];
  onSavePayload?: (payload: Record<string, any>) => void;
}

interface Segment {
  id: number;
  name: string;
  description: string;
  len: number; // Number of personas
  created_at: string;
  updated_at: string;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const TestUseCase: React.FC<InsightsFormProps> = ({
  onSubmit,
  selectedSegmentIds,
  personaFilters,
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  dynamicSegmentationId,
  dynamicSegmentIndices = [],
  onSavePayload,
}) => {
  const { audienceData } = useAudience();
  const [_segments, setSegments] = useState<Segment[]>([]);

  // Initialize form state from initialFormData
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  const [context, setContext] = useState(initialFormData.context || '');

  // Image-related state
  const [_imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(initialFormData.imagePreview || '');
  const [imageDescription, setImageDescription] = useState(initialFormData.imageDescription || '');

  const [_loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update parent component with form data changes
  useEffect(() => {
    const formData = {
      simName,
      goal,
      imagePreview,
      imageDescription,
      context,
    };

    onFormDataChange(formData);
  }, [simName, goal, imagePreview, imageDescription, context]); // Remove onFormDataChange from dependencies

  // Handle field changes and notify parent about edits
  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
  ) => {
    setter(value);
    if (onEditStep) {
      onEditStep();
    }
  };

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type against the shared Gemini-supported formats
    if (
      !ALLOWED_IMAGE_MIME_TYPES.includes(file.type) &&
      !ALLOWED_IMAGE_EXTENSIONS_RE.test(file.name)
    ) {
      setError(`"${file.name}" isn't a supported image. ${UPLOAD_HINT_TEXT_SINGLE}`);
      return;
    }

    // Validate file size against the shared cap
    if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      setError(`Image size must be less than ${MAX_UPLOAD_SIZE_MB}MB`);
      return;
    }

    setImageFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    if (onEditStep) {
      onEditStep();
    }
  };

  // Remove uploaded image
  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageDescription('');
    if (onEditStep) {
      onEditStep();
    }
  };

  useEffect(() => {
    const fetchSegments = async () => {
      if (!audienceData.audienceId) return;

      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/audience/${audienceData.audienceId}/segments`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch segments');
        }

        const data = await response.json();
        // Filter segments to only include the selected ones
        const filteredSegments = data.filter((segment: Segment) =>
          selectedSegmentIds.includes(segment.id),
        );
        setSegments(filteredSegments);
        setError(null);
      } catch (err) {
        console.error('Error fetching segments:', err);
        setError('Failed to load segments. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSegments();
  }, [audienceData.audienceId, selectedSegmentIds]);

  const saveMode = !!onSavePayload;
  // Edit & Rerun of a DYNAMIC-segmentation sim passes dynamicSegment* instead of segment
  // ids (contentData.segment_ids is [] for such a sim). Without this the gate below could
  // never become true and Run stayed disabled forever, with the dynamic scope lost
  // (#468 F3-03). EditRerunModal hands these to every form it renders.
  const isFormValid =
    (saveMode || selectedSegmentIds.length > 0 || dynamicSegmentIndices.length > 0) &&
    simName.trim() !== '';

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const simulationData: any = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        ...(dynamicSegmentationId
          ? {
              dynamic_segmentation_id: dynamicSegmentationId,
              dynamic_segment_indices: dynamicSegmentIndices,
            }
          : {}),
        name: simName,
        persona_filters: personaFilters,
        task: 'test_use_case',
        context: context,
        goal: goal,
      };

      if (imagePreview) {
        simulationData.images = [imagePreview];
        simulationData.image_descriptions = [
          imageDescription || 'User uploaded image for analysis',
        ];
      }

      if (onSavePayload) {
        onSavePayload(simulationData);
        return;
      }

      const response = await fetch(`${API_URL}/simulations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(simulationData),
      });

      if (!response.ok) {
        throw new Error('Failed to start simulation');
      }

      const data = await response.json();

      // Call onSubmit with the simulation ID instead of navigating away
      onSubmit(data.simulation_id);
    } catch (err) {
      console.error('Error starting simulation:', err);
      setError('Failed to start simulation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">
            {selectedSegmentIds.length} segments selected
          </span>
        </div>

        {error ? <div className="text-sm text-red-600 mb-3">{error}</div> : null}
      </div>

      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name <span className="text-red-500">*</span>
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => handleFieldChange(setSimName, e.target.value)}
          placeholder="Enter a name for your simulation"
          required
        />
      </div>

      <div className="mb-6">
        <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
          Goal
        </label>
        <input
          id="goal"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={goal}
          onChange={(e) => handleFieldChange(setGoal, e.target.value)}
          placeholder="What do you want to achieve with this simulation?"
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Image for Analysis (Optional)
        </label>

        {!imagePreview ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              id="imageUpload"
              accept={ACCEPTED_IMAGE_INPUT}
              onChange={handleImageUpload}
              className="hidden"
            />
            <label htmlFor="imageUpload" className="cursor-pointer flex flex-col items-center">
              <Upload className="w-12 h-12 text-gray-400 mb-3" />
              <span className="text-sm font-medium text-gray-700">Click to upload image</span>
              <span className="text-xs text-gray-500 mt-1">{UPLOAD_HINT_TEXT_SINGLE}</span>
            </label>
          </div>
        ) : (
          <div className="relative">
            <div className="relative border border-gray-300 rounded-lg overflow-hidden">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-64 object-contain bg-gray-50"
              />
              <button
                onClick={removeImage}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-3">
              <label
                htmlFor="imageDescription"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Image Description (Optional)
              </label>
              <input
                id="imageDescription"
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={imageDescription}
                onChange={(e) => handleFieldChange(setImageDescription, e.target.value)}
                placeholder="Brief description of what the image shows"
              />
            </div>
          </div>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-2">
          Context (Optional)
        </label>
        <textarea
          id="context"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                  focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          value={context}
          onChange={(e) => handleFieldChange(setContext, e.target.value)}
          placeholder="Any additional context about what you're trying to learn"
        />
      </div>

      <div className="flex justify-between items-center">
        <div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!isFormValid && !error && (
            <p className="text-sm text-gray-500">
              {selectedSegmentIds.length === 0
                ? 'Please select at least one segment'
                : simName.trim() === ''
                  ? 'Please enter a simulation name'
                  : ''}
            </p>
          )}
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? 'Starting...' : saveMode ? 'Continue' : 'Start Simulation'}
        </Button>
      </div>
    </div>
  );
};

export default TestUseCase;
